import type { MeetChatImageAttachment } from './types';

/** Меньше пикселей и размер файла — быстрее сжатие и доставка через byte stream. */
const MAX_IMAGE_DIMENSION = 720;
const INITIAL_OUTPUT_QUALITY = 0.68;
const MAX_FILE_SIZE_BYTES = 48_000;
const OUTPUT_MIME_TYPE = 'image/jpeg';

const loadImageElement = (source: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();

    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Не удалось загрузить изображение'));
    image.src = source;
  });

const getScaledSize = (width: number, height: number) => {
  if (width <= MAX_IMAGE_DIMENSION && height <= MAX_IMAGE_DIMENSION) {
    return { width, height };
  }

  const scale = Math.min(MAX_IMAGE_DIMENSION / width, MAX_IMAGE_DIMENSION / height);

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

const canvasToBlob = (canvas: HTMLCanvasElement, quality: number) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error('Не удалось подготовить изображение'));
      },
      OUTPUT_MIME_TYPE,
      quality,
    );
  });

const drawImageToCanvas = async (file: File) => {
  if (typeof createImageBitmap === 'function') {
    try {
      const probe = await createImageBitmap(file);
      const { width, height } = getScaledSize(probe.width, probe.height);
      probe.close();

      const bitmap = await createImageBitmap(file, {
        resizeWidth: width,
        resizeHeight: height,
        resizeQuality: 'medium',
      });
      const outputWidth = bitmap.width;
      const outputHeight = bitmap.height;

      const canvas = document.createElement('canvas');
      canvas.width = outputWidth;
      canvas.height = outputHeight;

      const context = canvas.getContext('2d');

      if (!context) {
        bitmap.close();
        throw new Error('Не удалось подготовить изображение');
      }

      context.drawImage(bitmap, 0, 0);
      bitmap.close();

      return { canvas, width: outputWidth, height: outputHeight };
    } catch {
      // fallback ниже
    }
  }

  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await loadImageElement(objectUrl);
    const { width, height } = getScaledSize(image.width, image.height);
    const canvas = document.createElement('canvas');

    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Не удалось подготовить изображение');
    }

    context.drawImage(image, 0, 0, width, height);

    return { canvas, width, height };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

export const prepareMeetChatImageAttachment = async (file: File): Promise<MeetChatImageAttachment> => {
  if (!file.type.startsWith('image/')) {
    throw new Error('Можно отправлять только изображения');
  }

  const { canvas, width, height } = await drawImageToCanvas(file);

  let quality = INITIAL_OUTPUT_QUALITY;
  let blob = await canvasToBlob(canvas, quality);

  while (blob.size > MAX_FILE_SIZE_BYTES && quality > 0.42) {
    quality -= 0.08;
    blob = await canvasToBlob(canvas, quality);
  }

  const previewUrl = URL.createObjectURL(blob);
  const jpegFile = new File([blob], 'chat-image.jpg', { type: OUTPUT_MIME_TYPE });

  return {
    dataUrl: previewUrl,
    mimeType: OUTPUT_MIME_TYPE,
    width,
    height,
    file: jpegFile,
  };
};
